import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { hostAvatarUrl, podcastAssetUrl } from './podcastApi';

// Jeden wspólny odtwarzacz próbek głosu (start nowej próbki zatrzymuje poprzednią).
export const useSamplePlayer = () => {
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (error) {
        // jsdom / przeglądarki bez audio
      }
    }
    setPlayingId(null);
  }, []);

  const toggle = useCallback((host) => {
    if (playingId === host.id) {
      stop();
      return;
    }
    const src = podcastAssetUrl(host.sample);
    if (!src || typeof Audio === 'undefined') return;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (error) {
        // ignorujemy
      }
    }
    const audio = new Audio(src);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audioRef.current = audio;
    setPlayingId(host.id);
    try {
      const result = audio.play();
      if (result && typeof result.catch === 'function') result.catch(() => setPlayingId(null));
    } catch (error) {
      setPlayingId(null);
    }
  }, [playingId, stop]);

  useEffect(() => () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (error) {
        // ignorujemy
      }
    }
  }, []);

  return { playingId, toggle };
};

const Avatar = ({ host }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-base font-semibold text-primary-700">
        {(host.name || host.id || '?').charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={hostAvatarUrl(host.id)}
      alt=""
      className="h-12 w-12 flex-shrink-0 rounded-full object-cover bg-gray-100"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Wybór prowadzącego dla roli A (prowadzący/a) albo B (ekspert/ka).
 * Osoba wybrana w drugiej roli jest nieaktywna (hostA ≠ hostB).
 */
const HostPicker = ({ role, hosts, value, otherValue, onChange, disabled, player }) => {
  const { t } = useTranslation();
  const groupId = `podcast-host-${role}`;

  return (
    <fieldset>
      <legend id={`${groupId}-legend`} className="block text-sm font-medium text-gray-700 mb-2">
        {t(`podcasts.form.host${role}Label`)}
      </legend>
      <div role="radiogroup" aria-labelledby={`${groupId}-legend`} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={groupId}>
        {hosts.map((host) => {
          const selected = value === host.id;
          const takenByOther = otherValue === host.id;
          const inputId = `${groupId}-${host.id}`;
          const playing = player?.playingId === host.id;
          return (
            <div
              key={host.id}
              className={`relative rounded-xl border p-3 transition-colors ${
                selected
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : takenByOther
                    ? 'border-gray-200 bg-gray-50 opacity-60'
                    : 'border-gray-200 bg-white hover:border-primary-300'
              }`}
            >
              <label htmlFor={inputId} className={`flex items-start gap-3 pr-10 ${takenByOther || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  id={inputId}
                  type="radio"
                  name={groupId}
                  value={host.id}
                  checked={selected}
                  disabled={disabled || takenByOther}
                  onChange={() => onChange(host.id)}
                  className="sr-only"
                />
                <Avatar host={host} />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">{host.name}</span>
                  {host.tagline && <span className="mt-0.5 block text-xs leading-5 text-gray-600">{host.tagline}</span>}
                  {takenByOther && (
                    <span className="mt-1 block text-[11px] text-gray-500">
                      {t(`podcasts.form.takenBy${role === 'A' ? 'B' : 'A'}`)}
                    </span>
                  )}
                </span>
              </label>
              {host.sample && player && (
                <button
                  type="button"
                  onClick={() => player.toggle(host)}
                  style={{ top: 12, right: 12 }}
                  className="absolute inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-primary-600 hover:bg-primary-50"
                  aria-label={t(playing ? 'podcasts.form.stopSample' : 'podcasts.form.playSample', { name: host.name })}
                  aria-pressed={playing}
                >
                  {playing ? <PauseIcon className="h-4 w-4" aria-hidden="true" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" />}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

export default HostPicker;
